import React, { useState } from 'react';
import { ArrowRight, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export async function saveEmailLead(email: string) {
  if (!email) return { error: new Error('No email provided') };

  const { data, error, status } = await supabase
    .from('subscriptions')
    .upsert(
      {
        email,
        status: 'email_captured',      // matches the RLS policy
        roast_type: 'medium',        // Default roast type to satisfy not-null constraint
        subscription_months: 3,      // Default to 3 months to satisfy check constraint
      },
      { onConflict: 'email', ignoreDuplicates: true }
    )
    .select(); // optional: returns the row

  if (error) {
    console.error('Supabase upsert error', { status, error });
    return { error };
  }
  return { data };
}

interface EmailCaptureProps {
  onSubmit: (email: string) => void;
}

export default function EmailCapture({ onSubmit }: EmailCaptureProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      // Save email using the new saveEmailLead function
      const { error } = await saveEmailLead(email);

      if (error) {
        console.error('Error saving email:', error);
        // Continue anyway - don't block the user flow
      }
    } catch (err) {
      console.error('Error saving email:', err);
      // Continue anyway - don't block the user flow
    }
    
    onSubmit(email);
    setIsSubmitting(false);
  };

  return (
    <div className="text-center space-y-6 px-4">
      <div className="relative">
        <Mail className="w-12 h-12 md:w-16 md:h-16 text-[#fda36b] mx-auto mb-4 md:mb-6" />
        <div className="absolute inset-0 bg-[#fda36b]/20 blur-xl rounded-full" />
      </div>

      <div className="space-y-3 md:space-y-4">
        <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
          We think we know your type (coffee-wise).
        </h2>
        <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-sm md:max-w-md mx-auto px-2">
          Drop your email so we can save your result, send occasional caffeine-fueled updates, 
          and maybe even a discount. No spam. We're far too lazy for that.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-sm md:max-w-md mx-auto px-2">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@regretslater.com"
            className="flex-1 px-4 py-3 bg-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#fda36b] placeholder-white/50 text-white text-center sm:text-left border border-white/20 focus:border-[#fda36b]"
            required
            autoComplete="email"
            autoFocus
          />
          <button
            type="submit"
            disabled={isSubmitting || !email}
            className="bg-[#fda36b] text-[#132947] px-6 py-3 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {isSubmitting ? (
              'Loading...'
            ) : (
              <>
                <span className="hidden sm:inline">Show Me My Match</span>
                <span className="sm:hidden">Get My Match</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}